import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import tipoMonedaService, { TipoMoneda } from '../../services/tipoMonedaService';

const Monedas: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [monedas, setMonedas] = useState<TipoMoneda[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [_loading, setLoading] = useState(false);

  const fetchMonedas = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const data = await tipoMonedaService.getTiposMonedaPaged({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search || undefined,
      });
      setMonedas(data.items);
      setTotalCount(data.totalCount);
    } catch (error: unknown) {
      console.error('Error al cargar monedas:', error);
      enqueueSnackbar('Error al cargar monedas', { variant: 'error' });
      setMonedas([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonedas();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchMonedas(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchMonedas(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchMonedas(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (codigoMoneda: number) => {
    if (window.confirm('¿Está seguro de eliminar esta moneda?')) {
      try {
        await tipoMonedaService.deleteTipoMoneda(codigoMoneda);
        enqueueSnackbar('Moneda eliminada exitosamente', { variant: 'success' });
        fetchMonedas(page, rowsPerPage, searchTerm);
      } catch (error: unknown) {
        console.error('Error al eliminar moneda:', error);
        const err = error as { response?: { data?: string | { message?: string } } };
        const msg = typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.response?.data?.message || 'Error al eliminar moneda';
        enqueueSnackbar(msg, { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Monedas
            </Typography>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/sistema/monedas/nuevo')}>
              Nueva Moneda
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por código, descripción o símbolo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {monedas.map((moneda) => (
                <Card key={moneda.codigoMoneda} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {moneda.codigoMoneda} - {moneda.descripcion}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Símbolo: {moneda.simbolo || '-'}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Cotizaciones">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/sistema/movmoneda?codigoMoneda=${moneda.codigoMoneda}`)}
                          color="primary"
                        >
                          <TrendingUpIcon />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => navigate(`/sistema/monedas/${moneda.codigoMoneda}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(moneda.codigoMoneda)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Símbolo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>ID FCE</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monedas.map((moneda) => (
                    <TableRow key={moneda.codigoMoneda} hover>
                      <TableCell>{moneda.codigoMoneda}</TableCell>
                      <TableCell>{moneda.descripcion}</TableCell>
                      <TableCell>{moneda.simbolo || '-'}</TableCell>
                      <TableCell>{moneda.idfce || '-'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Cotizaciones">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/sistema/movmoneda?codigoMoneda=${moneda.codigoMoneda}`)}
                            color="primary"
                          >
                            <TrendingUpIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/sistema/monedas/${moneda.codigoMoneda}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(moneda.codigoMoneda)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Monedas;
